import { useState, useRef, useMemo, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '../../../../contexts/hooks';
import { formatMessage } from 'devextreme/localization';
import Toolbar, { Item } from 'devextreme-react/toolbar';
import DataSource from 'devextreme/data/data_source';
import Datagrid, { Column, Selection, Lookup as LookupGrid, GroupPanel, Grouping, Scrolling, Editing, Popup, Form, RequiredRule, Button as ButtonGrid,} from 'devextreme-react/data-grid';
import { handleStatus, handleForwarded } from './Functions/Studies';
import { NewTemporalRemission, newToast, LabRefChip } from '../../..';
import { ContextMenu, ScrollView, Tooltip } from 'devextreme-react';
import Button from 'devextreme-react/button';
import { ordPending, ordCheck, ordFactureItem, ordDeleteStudy, ordBarcode, ordCounterExams, ordViewStudiesDetail, ordForwardedYes, Pin, UnPin } from '../../../../images/icons';
import { ResultEstadoResult, StudiesAddType, SysIResultados } from '../../../../types/types.orders';
import { addStudy, updateStateAndRem, setOrdEstRpmData, removeStudy, setPanelVisible, setPanelPined } from '../../../../contexts/slices/orders';
import { SystemFields } from '../../../../types/types.config';
import CustomStore from 'devextreme/data/custom_store';
import { useScreenSize } from '../../../../utils/media-query';
import { ModalConfirmation } from '../../..';
import useApiRem from '../../../../api/api.remmisions';
import { UseApiResults } from '../../../../api/api.results';
import { shallowEqual } from 'react-redux';
import { useApiFunctionsResult } from './Functions/Results';
import { StudiesSearchSelect, ProfilesSearchSelect } from './components';
import type { StudyItem, ProfileItem } from './components';

const Index = () => {
  //#region DECLARACIONES Y VARIABLES
  const { isSmall, isXSmall } = useScreenSize();
  const idOrd = useAppSelector((x) => x.globalOrders.CurrentOrder.IdOrder);
  const mode = useAppSelector((x) => x.globalOrders.CurrentOrder.Mode);
  const currentData = useAppSelector((x) => x.globalOrders.CurrentOrder.OrderData);
  const ModuleRemisions: boolean = JSON.parse(useAppSelector((x) => x.configuration.Data?.Config_General.filter((x) => x.Cgn_Nombre === 'ModuleRemisions')[0].Cgn_Resultado, shallowEqual) || 'false');
  const ModuleBarcode: boolean = JSON.parse(useAppSelector((x) => x.configuration.Data?.Config_General.filter((x) => x.Cgn_Nombre === 'ModuleBarcode')[0].Cgn_Resultado, shallowEqual) || 'false');
  const ModuleBilling: boolean = JSON.parse(useAppSelector((x) => x.configuration.Data?.Config_General.filter((x) => x.Cgn_Nombre === 'ModuleBilling')[0].Cgn_Resultado, shallowEqual) || 'false');
  const infoAditionalFields = useAppSelector((x) => x.configuration.Data?.SystemFields, shallowEqual)
    ?.filter((x) => x.Dfc_Tabla === 'Estudios' && x.Dfc_Estado === true)
    .sort((a, b) => a.Dfc_Orden - b.Dfc_Orden);
  const Prm_Ord_OEr = useAppSelector((x) => x.userInfo.Permissions?.Prm_Ord_OEr);
  const Prm_Ord_Remisiones_Tmp = useAppSelector((x) => x.userInfo.Permissions?.Prm_Ord_Remisiones_Tmp);
  const Prm_CodBarImprimir = useAppSelector((x) => x.userInfo.Permissions?.Prm_CodBarImprimir, shallowEqual) ?? false;
  const Areas = useAppSelector((x) => x.configuration.Data?.Areas);
  const cmbLabRef = useAppSelector((x) => x.configuration.Data?.LabReferencia) || [];

  // Datos necesarios para manejar perfiles
  const profiles = useAppSelector((x) => x.configuration.Data?.Perfiles || [], shallowEqual);
  const StateStudyNewOrder = JSON.parse(useAppSelector((x) => x.configuration.Data?.Config_General.filter((x) => x.Cgn_Nombre === 'InitialStateTestForNewOrder')[0].Cgn_Resultado, shallowEqual) || 'false');

  const dataGridRef = useRef<Datagrid>(null);
  const dataGridProfRef = useRef<Datagrid>(null);
  const { getResults } = UseApiResults();
  const [selectedStudie, setSelectedStudie] = useState<{ state: boolean; id: number } | null>(null);
  const addedStudies = useAppSelector((x) => x.globalOrders.CurrentOrder.StudiesData);
  const dispatch = useAppDispatch();

  // APIs necesarias para los handlers
  const { getTemporalRemisions } = useApiRem();

  // Estados y constantes para componentes genéricos
  const [modalRemVisible, setModalRemVisible] = useState<boolean>(false);
  const [modalRemConfVisible, setModalRemConfVisible] = useState<boolean>(false);
  const panelVisible = useAppSelector((x) => x.globalOrders.PanelVisible, shallowEqual);
  const { handlePrintBarcodesLabel } = useApiFunctionsResult();
  const handlePanelClose = () => {
    dispatch(setPanelVisible(true));
    dispatch(setPanelPined(false));
  };

  // Convertir estudios agregados a formato StudyItem para evitar duplicados
  const addedStudiesAsItems = useMemo((): StudyItem[] => {
    return addedStudies.map((study) => ({
      id: study.Es_Id,
      idlr: study.Es_Lr_Id || null,
      idAr: study.Es_Aa_Id,
      code: study.Es_Codigo,
      name: study.Es_Nombre,
      group: 'Estudios', // Campo genérico por ahora
      isActive: true,
    }));
  }, [addedStudies]);

  // Handler para selección de estudio con nuevo componente
  const handleStudySelected = useCallback(
    async (study: StudyItem | null) => {
      if (!study) {
        return; // No hacer nada si se limpia la selección
      }
      try {
        if (currentData && currentData.Ord_POrd_Id && currentData.Ord_POrd_Id !== 0 && currentData.Ord_POrd_Id !== null) {
          newToast('WARNING', 'No es posible eliminar ni agregar estudios a una orden que fué creada desde una preorden', 5);
          return;
        }
        const studyToAdd: StudiesAddType = {
          Es_Id: study.id as number,
          Es_Lr_Id: study.idlr as number,
          Es_Codigo: study.code,
          Es_Aa_Id: study.idAr as number,
          Es_Nombre: study.name,
          Estado: true,
          Remitido: false,
          Accion: 1, // Acción de agregar
          Editado: false,
        };

        dispatch(addStudy(studyToAdd));
        newToast('SUCCESS', `Estudio '${study.name}' agregado a la orden`, 3);
      } catch (error) {
        console.error('Error adding study to order:', error);
        newToast('ERROR', 'Error al agregar el estudio a la orden', 3);
      }
    },
    [currentData, dispatch]
  );

  // Handler para selección de perfil con nuevo componente
  const handleProfileSelected = useCallback(
    async (profile: ProfileItem | null) => {
      if (!profile) {
        return; // No hacer nada si se limpia la selección
      }
      try {
        let selectedProfile: any;

        if (currentData && currentData.Ord_POrd_Id && currentData.Ord_POrd_Id !== 0 && currentData.Ord_POrd_Id !== null) {
          newToast('WARNING', 'No es posible eliminar ni agregar estudios a una orden que fué creada desde una preorden', 5);
          return;
        }

        // Si tenemos los datos originales del perfil, usarlos directamente
        if (profile.originalData) {
          selectedProfile = profile.originalData;
        } else {
          // Fallback: agrupar perfiles por Rpe_Pfe_Id como en el hook original
          const profilesGrouped =
            profiles?.reduce((acumulador: any, actual) => {
              const { Rpe_Pfe_Id, SysI_Estudios, SysI_PerfilesEstudios, ...resto } = actual;
              const objetoAgrupado = acumulador.find((elem) => elem.Rpe_Pfe_Id === Rpe_Pfe_Id);

              if (objetoAgrupado) {
                objetoAgrupado.SysI_Estudios.push(SysI_Estudios);
              } else {
                acumulador.push({
                  Rpe_Pfe_Id,
                  SysI_PerfilesEstudios,
                  SysI_Estudios: [SysI_Estudios],
                });
              }
              return acumulador;
            }, []) || [];

          selectedProfile = profilesGrouped.find((p: any) => p.Rpe_Pfe_Id === profile.id);
        }

        if (!selectedProfile) {
          newToast('ERROR', 'Perfil no encontrado', 3);
          return;
        }

        // Verificar si el perfil ya fue agregado
        if (addedStudies.some((x) => x.Rpe_Pfe_Id === selectedProfile.Rpe_Pfe_Id)) {
          newToast('INFO', `El perfil '${selectedProfile.SysI_PerfilesEstudios.Pfe_Nombre}' ya ha sido agregado`, 5);
          return;
        }

        // Agregar todos los estudios del perfil
        for (const element of selectedProfile.SysI_Estudios) {
          if (addedStudies.some((x) => x.Es_Id === element.Es_Id)) {
            newToast('INFO', `El estudio '${element.Es_Nombre}' ya ha sido agregado`, 3);
            continue;
          }

          const studyToAdd: StudiesAddType = {
            Estado: StateStudyNewOrder,
            Es_Id: element.Es_Id,
            Es_Codigo: element.Es_Codigo,
            Es_Nombre: element.Es_Nombre,
            Es_Lr_Id: element.Es_Lr_Id,
            Es_Aa_Id: element.Es_Aa_Id || null,
            Rpe_Pfe_Id: selectedProfile.Rpe_Pfe_Id,
            Remitido: false,
            Accion: 1,
            Editado: false,
            Profile: selectedProfile.SysI_PerfilesEstudios.Pfe_Nombre,
          };

          // Verificar remisiones temporales
          try {
            const resp = await getTemporalRemisions(element.Es_Id);
            if (resp) {
              studyToAdd.Remitido = true;
            }
          } catch (error) {
            console.warn('Error checking temporal remissions:', error);
          }

          dispatch(addStudy(studyToAdd));
        }

        newToast('SUCCESS', `Perfil '${selectedProfile.SysI_PerfilesEstudios.Pfe_Nombre}' agregado con ${selectedProfile.SysI_Estudios.length} estudios`, 3);
      } catch (error) {
        console.error('Error adding profile to order:', error);
        newToast('ERROR', 'Error al agregar el perfil a la orden', 3);
      }
    },
    [currentData, profiles, addedStudies, StateStudyNewOrder, getTemporalRemisions, dispatch]
  );
  //#endregion

  //#region HANDLERS PARA LA FUNCIONALIDAD PRINCIPAL
  const handleSelectionChange = (e) => {
    dataGridProfRef.current?.instance.clearSelection();
    const selected = dataGridRef.current?.instance.getSelectedRowsData();
    if (selected && selected.length > 0) {
      setSelectedStudie({ state: selected[0].Estado, id: e.selectedRowKeys[0] });
    }
  };

  const handleSelectionChangeProfile = (e) => {
    dataGridRef.current?.instance.clearSelection();
    const selected = dataGridProfRef.current?.instance.getSelectedRowsData();
    if (selected && selected.length > 0) {
      setSelectedStudie({ state: selected[0].Estado, id: e.selectedRowKeys[0] });
    }
  };

  const handleClicChangeStatus = () => {
    if (selectedStudie?.id) {
      dispatch(updateStateAndRem({ Es_Id: selectedStudie.id, type: 'State', state: undefined }));
    }
    dataGridRef.current?.instance.clearSelection();
    dataGridProfRef.current?.instance.clearSelection();
    setSelectedStudie(null);
  };

  const handleClicChangeForwared = async () => {
    if (selectedStudie?.id) {
      if (!addedStudies.find((x) => x.Es_Id === selectedStudie.id)?.Remitido && ModuleRemisions && Prm_Ord_Remisiones_Tmp) {
        const resp = await getTemporalRemisions(selectedStudie.id);
        if (resp === null) {
          setModalRemConfVisible(true);
          return;
        }
      }
      dispatch(updateStateAndRem({ Es_Id: selectedStudie.id, type: 'Rem', state: undefined }));
    }
    dataGridRef.current?.instance.clearSelection();
    dataGridProfRef.current?.instance.clearSelection();
    setSelectedStudie(null);
  };

  const handleClickDeleteStudy = () => {
    if (idOrd !== -1) {
      // Si la orden tiene el campo Ord_POrd_Id y el valor es diferente de 0, significa que la orden fue creada desde una preorden, por lo que no se puede editar.
      if (currentData && currentData.Ord_POrd_Id && currentData.Ord_POrd_Id !== 0 && currentData.Ord_POrd_Id !== null) {
        newToast('WARNING', 'No es posible eliminar ni agregar estudios a una orden que fué creada desde una preorden', 5);
        return;
      }
      getResults().then((x) => {
        const result: SysIResultados[] = x;
        if (selectedStudie && result.filter((x) => x.Res_Es_Id === selectedStudie.id && x.Res_Estado > ResultEstadoResult.SinResultado).length > 0) {
          newToast('WARNING', 'El estudio seleccionado ya cuenta con resultados ingresados.', 5);
          return;
        }
        if (selectedStudie?.id) {
          dispatch(removeStudy(selectedStudie.id));
        }
      });
    } else {
      if (selectedStudie?.id) {
        dispatch(removeStudy(selectedStudie.id));
      }
    }
  };

  const handleUpdateRow = (e) => {};

  const customDataSource = new CustomStore({
    key: 'Es_Id',
    loadMode: 'raw',
    load: () => {
      return new Promise((resolve) => {
        const studies = addedStudies.filter((x) => !x.Rpe_Pfe_Id);
        resolve(studies);
      });
    },
    update: (key, values) => {
      return updateFields(key, values);
    },
  });

  const customSrcEstudios = new DataSource({
    store: customDataSource,
    reshapeOnPush: true,
  });

  const customDataSourceProfiles = new CustomStore({
    key: 'Es_Id',
    load: () => {
      return new Promise((resolve) => {
        const studies = addedStudies.filter((x) => x.Rpe_Pfe_Id);
        resolve(studies);
      });
    },
    update: (key, values) => {
      return updateFields(key, values);
    },
  });

  const updateFields = (key, values) => {
    return new Promise((resolve) => {
      const newStudies = addedStudies.map((study) => {
        if (study.Es_Id === key) {
          return { ...study, ...values };
        }
        return study;
      });
      Object.keys(values.AditionalFields).forEach((element) => {
        dispatch(setOrdEstRpmData({ NomParam: element, IdEst: key, Value: values.AditionalFields[element] }));
      });
      resolve(newStudies);
    });
  };

  const setModalRemmVisible = () => {
    setModalRemVisible(false);
    setModalRemConfVisible(false);
  };

  const onConfirmRemmisionModal = () => {
    setModalRemVisible(true);
  };

  const onNoConfirmRemmisionModal = () => {
    setModalRemConfVisible(false);
    if (selectedStudie) {
      dispatch(updateStateAndRem({ Es_Id: selectedStudie.id, type: 'Rem', state: true }));
    }
  };

  const setConfigTemRem = (isSaved: boolean) => {
    if (isSaved && selectedStudie) {
      dispatch(updateStateAndRem({ Es_Id: selectedStudie.id, type: 'Rem', state: true }));
    }
  };

  const handlePrintBarcode = useCallback(() => {
    if (!idOrd || idOrd === -1) {
      return;
    }
    if (!Prm_CodBarImprimir) {
      newToast('INFO', 'No cuenta con los permisos para imprimir etiquetas', 5);
      return;
    }
    handlePrintBarcodesLabel(idOrd);
  }, [idOrd, handlePrintBarcodesLabel]);

  const handlePrintBarcodeSelected = useCallback(async () => {
    if (!idOrd || idOrd === -1) {
      return;
    }
    if (!Prm_CodBarImprimir) {
      newToast('INFO', 'No cuenta con los permisos para imprimir etiquetas', 5);
      return;
    }

    if (selectedStudie?.id) {
      const LstIdsEstudios = [selectedStudie.id];
      handlePrintBarcodesLabel(idOrd, LstIdsEstudios);
    }
  }, [idOrd, selectedStudie?.id, handlePrintBarcodesLabel]);
  //#endregion

  return (
    <>
      <ModalConfirmation onConfirm={onConfirmRemmisionModal} onCancel={onNoConfirmRemmisionModal} visible={modalRemConfVisible} buttons='No/Yes' message={formatMessage('OrdConfirmationReferStudyTemporaly')} />
      <NewTemporalRemission idEst={selectedStudie?.id || -1} title={''} visible={modalRemVisible} setVisible={setModalRemmVisible} setConfigTemRem={setConfigTemRem} />
      <div style={{ height: 'calc(100vh - 280px)', paddingBottom: isSmall || isXSmall ? `150px` : `50px` }}>
        <div className='view-wrapper view-wrapper-dashboard'>
          <Toolbar>
            <Item location='before'>
              <StudiesSearchSelect onStudySelected={handleStudySelected} selectedStudies={addedStudiesAsItems} placeholder='Buscar estudios...' label={formatMessage('OrdEstAdd')} disabled={mode === 'loading' || mode === 'view'} className='comboboxItems' minSearchLength={1} />
            </Item>
            <Item location='before'>
              <ProfilesSearchSelect onProfileSelected={handleProfileSelected} selectedProfiles={[]} placeholder='Buscar perfiles...' label={formatMessage('OrdProfilesAdd')} disabled={mode === 'loading' || mode === 'view'} className='comboboxItems' minSearchLength={1} />
            </Item>
            {ModuleBilling && (
              <>
                <Item location='after' locateInMenu='auto'>
                  <div className='separator' />
                </Item>
                <Item location='after' widget='dxButton' showText='inMenu' locateInMenu='auto'>
                  <Button disabled={selectedStudie === null || mode === 'loading' || mode === 'view'} id='OrdInvoiceItem' icon={ordFactureItem} stylingMode='text' text='Facturar' />
                </Item>
              </>
            )}
            <Item location='after' locateInMenu='auto'>
              <div className='separator' />
            </Item>
            <Item location='after' widget='dxButton' showText='inMenu' locateInMenu='auto'>
              <Button disabled={selectedStudie === null || mode === 'loading' || mode === 'view'} id='OrdDeleteSelectedStudy' icon={ordDeleteStudy} stylingMode='text' onClick={handleClickDeleteStudy} text='Delete' />
            </Item>
            <Item location='after' widget='dxButton' showText='inMenu' locateInMenu='auto'>
              <Button disabled={selectedStudie === null || mode === 'loading' || mode === 'view'} id='OrdMarkSelectedStudyAsPending' onClick={handleClicChangeStatus} icon={selectedStudie && selectedStudie.state ? ordPending : ordCheck} text='Pending' stylingMode='text' />
            </Item>
            <Item location='after' locateInMenu='auto'>
              <div className='separator' />
            </Item>
            <Item location='after' visible={Prm_CodBarImprimir && ModuleBarcode} widget='dxButton' showText='inMenu' locateInMenu='auto'>
              <Button disabled={idOrd === -1 || mode !== 'view'} icon={ordBarcode} id='OrdPrintBarcode' stylingMode='text' text='Imprimir etiquetas' onClick={handlePrintBarcode} />
              <ContextMenu width={'auto'} target={'#OrdPrintBarcode'} dataSource={[{ text: 'Imprimir estudios seleccionados', icon: ordBarcode }]} onItemClick={handlePrintBarcodeSelected} />
              <Tooltip target={'#OrdPrintBarcode'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                Imprimir etiquetas para la orden
              </Tooltip>
            </Item>
            <Item location='after' widget='dxButton' showText='inMenu' locateInMenu='auto'>
              <Button disabled={selectedStudie === null || mode === 'loading' || mode === 'view'} icon={ordForwardedYes} id='OrdReferSelectedStudy' onClick={handleClicChangeForwared} stylingMode='text' text='Remitido' />
            </Item>
            <Item location='after' locateInMenu='never' showText='inMenu' widget='dxButton'>
              <Button id='showpanel' icon={panelVisible ? Pin : UnPin} text={panelVisible ? 'Panel Visible' : 'Panel oculto'} stylingMode='text' onClick={handlePanelClose} />
              <Tooltip target={'#showpanel'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                {panelVisible ? 'Ocultar panel' : 'Abrir panel'}
              </Tooltip>
            </Item>
          </Toolbar>
          <div style={{ marginTop: 20 }}>
            <ScrollView height={'calc(100vh - 360px)'}>
              <div>
                <div id='gridContainer'>
                  {addedStudies.filter((x) => !x.Rpe_Pfe_Id).length > 0 && (
                    <Datagrid onRowUpdated={handleUpdateRow} dataSource={customSrcEstudios} ref={dataGridRef} onSelectionChanged={handleSelectionChange} repaintChangesOnly={true}>
                      {infoAditionalFields && infoAditionalFields.length > 0 && Prm_Ord_OEr && (
                        <Editing allowUpdating={true} mode='popup' allowAdding={false} refreshMode='reshape'>
                          <Popup title={formatMessage('OrdModalOrdEstRpm')} showTitle showCloseButton fullScreen={isXSmall} width={500} height={'auto'} />
                          <Form colCount={1} showValidationSummary={true} />
                        </Editing>
                      )}
                      <Selection mode='single' />
                      <Scrolling mode='virtual' />
                      <GroupPanel visible={false} />
                      <Grouping autoExpandAll={true} />
                      <Column formItem={{ visible: false }} dataField='Es_Aa_Id' caption={formatMessage('OrdEstudyGridArea')} groupIndex={0}>
                        <LookupGrid dataSource={Areas} displayExpr={'Aa_Nombre'} valueExpr={'Aa_Id'} />
                      </Column>
                      <Column formItem={{ visible: false }} cellRender={handleStatus} width={80} caption={formatMessage('OrdEstudyGridState')} dataField='Estado' />
                      <Column formItem={{ visible: false }} width={80} caption={formatMessage('OrdEstudyGridCode')} dataField='Es_Codigo' />
                      <Column formItem={{ visible: false }} width={90} caption='LAB.REF' dataField='Es_Lr_Id' cellRender={(cellData) => renderLabRefChip(cellData, cmbLabRef)} alignment='center' />
                      <Column formItem={{ visible: false }} caption={formatMessage('OrdEstudyGridStudyName')} dataField='Es_Nombre' minWidth={200} />
                      <Column formItem={{ visible: false }} cellRender={handleForwarded} caption={formatMessage('OrdEstudyGridForwarded')} width={90} dataField='Remitido' />
                      {infoAditionalFields && infoAditionalFields.length > 0 && Prm_Ord_OEr && (
                        <Column type='buttons'>
                          <ButtonGrid name='edit' icon={ordViewStudiesDetail} cssClass='HandButtonGrid' hint={formatMessage('OrdViewAdditionalInfForStudy')} />
                        </Column>
                      )}
                      {infoAditionalFields && infoAditionalFields.length > 0 && Prm_Ord_OEr && renderFields(infoAditionalFields)}
                    </Datagrid>
                  )}
                  
                  {addedStudies.filter((x) => x.Rpe_Pfe_Id).length > 0 && (
                    <>
                      <h3 style={{ textAlign: 'center', color: '#03A9F4', fontSize: '15px', marginBottom: 0, fontWeight: 'bold' }}>{formatMessage('OrdProfilesAdd').toUpperCase()}</h3>
                      <Datagrid hoverStateEnabled={true} dataSource={customDataSourceProfiles} ref={dataGridProfRef} onSelectionChanged={handleSelectionChangeProfile} repaintChangesOnly={true}>
                        {infoAditionalFields && infoAditionalFields.length > 0 && Prm_Ord_OEr && (
                          <Editing allowUpdating={true} mode='popup' allowAdding={false}>
                            <Popup title={formatMessage('OrdModalOrdEstRpm')} showTitle showCloseButton fullScreen={isXSmall} width={500} height={'auto'} />
                            <Form colCount={1} showValidationSummary={true} />
                          </Editing>
                        )}
                        <Selection mode='single' />
                        <GroupPanel visible={false} />
                        <Scrolling mode='virtual' />
                        <Grouping autoExpandAll={true} />
                        <Column formItem={{ visible: false }} dataField='Profile' caption={formatMessage('OrdEstudyGridProfileGroup')} groupIndex={0} />
                        <Column formItem={{ visible: false }} cellRender={handleStatus} width={80} caption={formatMessage('OrdEstudyGridState')} dataField='Estado' />
                        <Column formItem={{ visible: false }} width={100} caption={formatMessage('OrdEstudyGridCode')} dataField='Es_Codigo' />
                        <Column formItem={{ visible: false }} width={90} caption='LAB.REF' dataField='Es_Lr_Id' cellRender={(cellData) => renderLabRefChip(cellData, cmbLabRef)} alignment='center' />
                        <Column formItem={{ visible: false }} caption={formatMessage('OrdEstudyGridStudyName')} dataField='Es_Nombre' minWidth={200} />
                        <Column formItem={{ visible: false }} visible={false} dataField='Rpe_Pfe_Id' />
                        <Column formItem={{ visible: false }} cellRender={handleForwarded} caption={formatMessage('OrdEstudyGridForwarded')} width={90} dataField='Remitido' />
                        {infoAditionalFields && infoAditionalFields.length > 0 && Prm_Ord_OEr && (
                          <Column type='buttons'>
                            <ButtonGrid name='edit' icon={ordViewStudiesDetail} cssClass='HandButtonGrid' hint={formatMessage('OrdViewAdditionalInfForStudy')} />
                          </Column>
                        )}
                        {infoAditionalFields && infoAditionalFields.length > 0 && Prm_Ord_OEr && renderFields(infoAditionalFields)}
                      </Datagrid>
                    </>
                  )}
                  <Toolbar>
                    <Item location='after' widget='dxButton' showText='inMenu' locateInMenu='auto'>
                      <span>
                        <i style={{ backgroundImage: `url(${ordCounterExams})`, backgroundSize: 'contain', backgroundRepeat: 'no-repeat', display: 'inline-block', width: '20px', height: '20px', verticalAlign: 'middle' }}></i>
                        {formatMessage('OrdTotalStudiesAdded')}: {addedStudies.length}
                      </span>
                    </Item>
                  </Toolbar>
                </div>
              </div>
            </ScrollView>

            <Tooltip showEvent='mouseenter' hideEvent='mouseleave' position={'top'} target={'#OrdInvoiceItem'}>
              {formatMessage('OrdInvoiceItem')}
            </Tooltip>
            <Tooltip showEvent='mouseenter' hideEvent='mouseleave' position={'top'} target={'#OrdDeleteSelectedStudy'}>
              {formatMessage('OrdDeleteSelectedStudy')}
            </Tooltip>
            <Tooltip showEvent='mouseenter' hideEvent='mouseleave' position={'top'} target={'#OrdMarkSelectedStudyAsPending'}>
              {selectedStudie?.state ? formatMessage('OrdMarkSelectedStudyAsPending') : formatMessage('OrdMarkSelectedStudyAsReceived')}
            </Tooltip>
            <Tooltip showEvent='mouseenter' hideEvent='mouseleave' position={'top'} target={'#OrdPrintBarcode'}>
              Imprimir etiquetas para la orden
            </Tooltip>
            <Tooltip showEvent='mouseenter' hideEvent='mouseleave' position={'top'} target={'#OrdReferSelectedStudy'}>
              {formatMessage('OrdReferSelectedStudy')}
            </Tooltip>
            <Tooltip showEvent='mouseenter' hideEvent='mouseleave' position={'top'} target={'#OrdViewAdditionalInfForStudy'}>
              {formatMessage('OrdViewAdditionalInfForStudy')}
            </Tooltip>
          </div>
        </div>
      </div>
    </>
  );
};

export default Index;

// Función para renderizar el chip de laboratorio de referencia
const renderLabRefChip = ({ data }: any, cmbLabRef: any[]) => {
  return <LabRefChip labRefId={data.Es_Lr_Id} labRefData={cmbLabRef} size='small' opacity={0.9} />;
};

const renderFields = (fields: SystemFields[] | undefined) => {
  if (!fields) {
    return null;
  }
  return fields
    ?.filter((x) => x.Dfc_Dinamico)
    .map((x) => {
      return (
        <Column key={`pr${x.Dfc_Id}`} caption={x.Dfc_Label} allowEditing={x.Dfc_Editable} formItem={{ label: { visible: false, alignment: 'center', location: 'top' }, visible:x.Dfc_Visible, editorType: x.TipoCampo.Tcm_Equivalencia, editorOptions: { labelLocation: 'left', stylingMode: 'outlined', labelMode: 'static', label: x.Dfc_Label } }} visible={false} dataField={`AditionalFields.${x.Dfc_Equivalencia}`}>
          {x.Dfc_Requerido && <RequiredRule />}
        </Column>
      );
    });
};
